import { useEffect, useState } from "react";

import { Button, Checkbox, Header, Message } from "semantic-ui-react";
import { styled } from "styled-components";

export type DiscussionPoll = {
  _id: string;
  title: string;
  allowMultiSelect: boolean;
  closed?: boolean;
  hasVoted?: boolean;
  myResponseOptionIds?: string[];
  totalVotes?: number;
  options: Array<{
    _id: string;
    title: string;
    voteCount?: number;
    percentage?: number;
  }>;
};

type DiscussionPollsProps = {
  commentId: string;
  polls: DiscussionPoll[];
  disabled?: boolean;
  voteOnPoll: (
    commentId: string,
    pollId: string,
    optionIds: string[],
  ) => Promise<void>;
  setPollClosed: (
    commentId: string,
    pollId: string,
    closed: boolean,
  ) => Promise<void>;
  canClosePoll: boolean;
};

const PollContainer = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  padding: 10px;
  margin-top: 8px;
`;

const ResultBarOuter = styled.div`
  height: 8px;
  background-color: #ececec;
  border-radius: 999px;
  overflow: hidden;
  width: 100%;
`;

const ResultBarInner = styled.div<{ $width: number }>`
  height: 100%;
  width: ${({ $width }) => `${$width}%`};
  background-color: #4a77d4;
  transition: width 0.2s ease-in-out;
  border-radius: 999px;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
`;

const OptionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.92em;
`;

const ResultBarRow = styled.div`
  margin-top: 4px;
  margin-bottom: 8px;
  margin-left: 22px;
`;

const OptionPercent = styled.div`
  min-width: 42px;
  text-align: right;
  color: #6a6a6a;
  font-size: 0.85em;
`;

const PollMeta = styled.div`
  color: #6a6a6a;
  font-size: 0.85em;
  line-height: 1em;
`;

const getInitialSelections = (polls: DiscussionPoll[]) => {
  const selections: Record<string, string[]> = {};
  for (const poll of polls) {
    selections[poll._id] = poll.myResponseOptionIds || [];
  }
  return selections;
};

export const DiscussionPolls = ({
  commentId,
  polls,
  disabled = false,
  voteOnPoll,
  setPollClosed,
  canClosePoll,
}: DiscussionPollsProps) => {
  const [selectionState, setSelectionState] = useState<
    Record<string, string[]>
  >(() => getInitialSelections(polls));
  const [savingPollIds, setSavingPollIds] = useState<Record<string, boolean>>(
    {},
  );

  useEffect(() => {
    setSelectionState(getInitialSelections(polls));
  }, [polls]);

  if (!polls.length) {
    return null;
  }

  const savePollVote = async (poll: DiscussionPoll, optionIds: string[]) => {
    setSavingPollIds((prev) => ({ ...prev, [poll._id]: true }));
    try {
      await voteOnPoll(commentId, poll._id, optionIds);
    } catch (_error) {
      setSelectionState(getInitialSelections(polls));
    } finally {
      setSavingPollIds((prev) => ({ ...prev, [poll._id]: false }));
    }
  };

  const updateDiscussionPollState = async (
    poll: DiscussionPoll,
    closed: boolean,
  ) => {
    setSavingPollIds((prev) => ({ ...prev, [poll._id]: true }));
    try {
      await setPollClosed(commentId, poll._id, closed);
    } finally {
      setSavingPollIds((prev) => ({ ...prev, [poll._id]: false }));
    }
  };

  return (
    <>
      {polls.map((poll) => {
        const selectedOptionIds =
          selectionState[poll._id] || poll.myResponseOptionIds || [];
        const isClosed = !!poll.closed;
        const totalVotes =
          poll.totalVotes ??
          (poll.options || []).reduce(
            (total, option) => total + (option.voteCount || 0),
            0,
          );
        const isSaving = !!savingPollIds[poll._id];
        // Allow users to see poll results before they have voted.
        // We treat "has results" as either the poll being closed or any option
        // having a non-zero percentage (computed server-side).
        const showResults =
          isClosed ||
          (poll.options || []).some((option) => (option.percentage ?? 0) > 0);
        const pollInteractionsDisabled = disabled || isSaving || isClosed;

        const updateSelection = (optionId: string) => {
          if (pollInteractionsDisabled) {
            return;
          }

          const isSelected = selectedOptionIds.includes(optionId);
          const nextSelections =
            poll.allowMultiSelect ?
              isSelected ? selectedOptionIds.filter((id) => id !== optionId)
              : [...selectedOptionIds, optionId]
            : isSelected ? []
            : [optionId];

          setSelectionState((prev) => ({
            ...prev,
            [poll._id]: nextSelections,
          }));
          void savePollVote(poll, nextSelections);
        };

        return (
          <PollContainer key={poll._id}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
                marginBottom: 10,
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <Header as="h5" style={{ marginBottom: 0 }}>
                  {poll.title}
                </Header>
                {canClosePoll ?
                  <PollMeta>
                    {totalVotes}{" "}
                    {totalVotes === 1 ? "person has" : "people have"} voted
                  </PollMeta>
                : null}
              </div>

              {canClosePoll ?
                <Button
                  size="tiny"
                  basic
                  loading={isSaving}
                  disabled={pollInteractionsDisabled}
                  onClick={() => updateDiscussionPollState(poll, !isClosed)}
                  content={isClosed ? "Reopen poll" : "Close poll"}
                />
              : null}
            </div>

            <div style={{ marginBottom: 12 }}>
              {(poll.options || []).map((option) => {
                const selected = selectedOptionIds.includes(option._id);

                return (
                  <div key={option._id}>
                    <OptionRow>
                      <OptionLabel>
                        <Checkbox
                          radio={!poll.allowMultiSelect}
                          checked={selected}
                          disabled={pollInteractionsDisabled}
                          label={option.title}
                          onClick={() => updateSelection(option._id)}
                        />
                      </OptionLabel>

                      <OptionPercent>
                        {showResults ?
                          <span>
                            {option.percentage || 0}%{" "}
                            {canClosePoll ? `(${option.voteCount})` : ""}
                          </span>
                        : <span>&nbsp;</span>}
                      </OptionPercent>
                    </OptionRow>
                    <ResultBarRow>
                      <ResultBarOuter>
                        <ResultBarInner
                          $width={showResults ? option.percentage || 0 : 0}
                        />
                      </ResultBarOuter>
                    </ResultBarRow>
                  </div>
                );
              })}
            </div>
            {isClosed ?
              <Message size="tiny" style={{ marginTop: 8, marginBottom: 0 }}>
                This poll is closed.
              </Message>
            : null}
          </PollContainer>
        );
      })}
    </>
  );
};
